import { useCallback, useEffect, useRef, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";

interface CameraCaptureModalProps {
  open: boolean;
  onClose: () => void;
  /** Photo prise : image carrée JPEG (data URL), prête comme une photo importée. */
  onCapture: (dataUrl: string) => void;
}

/** Côté de la photo enregistrée (carrée, comme sur les cartes scolaires). */
const OUTPUT_SIZE = 600;

type Facing = "user" | "environment";

function cameraError(err: unknown): string {
  const name = (err as { name?: string })?.name;
  if (name === "NotAllowedError" || name === "SecurityError")
    return "L'accès à la caméra a été refusé. Autorisez-le dans le navigateur (icône à gauche de l'adresse), puis réessayez.";
  if (name === "NotFoundError" || name === "OverconstrainedError") return "Aucune caméra n'a été trouvée sur cet appareil.";
  if (name === "NotReadableError") return "La caméra est déjà utilisée par une autre application.";
  return "Impossible d'ouvrir la caméra.";
}

/**
 * Prise de photo par la caméra (téléphone, tablette ou webcam) : aperçu en direct dans un cadre carré,
 * choix de la caméra avant/arrière, puis « Reprendre » ou « Utiliser cette photo ». Fonctionne en HTTPS
 * (ou sur localhost), comme l'exigent les navigateurs.
 */
export default function CameraCaptureModal({ open, onClose, onCapture }: CameraCaptureModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // Sur téléphone, la caméra arrière photographie l'élève ; sur ordinateur, seule la webcam existe.
  const [facing, setFacing] = useState<Facing>("environment");
  const [cameraCount, setCameraCount] = useState(0);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shot, setShot] = useState<string | null>(null);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setReady(false);
  }, []);

  useEffect(() => {
    if (!open || shot) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Ce navigateur ne permet pas d'utiliser la caméra ici (connexion non sécurisée ou navigateur trop ancien).");
      return;
    }
    let cancelled = false;
    setError(null);
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: { ideal: facing }, width: { ideal: 1280 }, height: { ideal: 960 } }, audio: false })
      .then(async (stream) => {
        if (cancelled) return stream.getTracks().forEach((track) => track.stop());
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          // « Prêt » dès la première image (onLoadedData) : play() peut tarder sur certains appareils.
          videoRef.current.play().catch(() => undefined);
        }
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!cancelled) setCameraCount(devices.filter((d) => d.kind === "videoinput").length);
      })
      .catch((err) => !cancelled && setError(cameraError(err)));
    return () => {
      cancelled = true;
      stop();
    };
  }, [open, facing, shot, stop]);

  // Fermeture : tout est remis à zéro et la caméra est libérée.
  useEffect(() => {
    if (open) return;
    stop();
    setShot(null);
    setError(null);
  }, [open, stop]);

  const take = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const side = Math.min(video.videoWidth, video.videoHeight);
    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // Carré central, comme le cadre affiché.
    ctx.drawImage(
      video,
      (video.videoWidth - side) / 2,
      (video.videoHeight - side) / 2,
      side,
      side,
      0,
      0,
      OUTPUT_SIZE,
      OUTPUT_SIZE,
    );
    setShot(canvas.toDataURL("image/jpeg", 0.88));
    stop();
  };

  const mirrored = facing === "user" || cameraCount <= 1;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Prendre une photo"
      subtitle="Cadrez le visage dans le carré, de face, sur un fond clair"
      icon="ri-camera-line"
      size="sm"
      footer={
        shot ? (
          <>
            <Button variant="outline" icon="ri-refresh-line" onClick={() => setShot(null)}>
              Reprendre
            </Button>
            <Button
              icon="ri-check-line"
              onClick={() => {
                onCapture(shot);
                onClose();
              }}
            >
              Utiliser cette photo
            </Button>
          </>
        ) : (
          <>
            {cameraCount > 1 && (
              <Button
                variant="outline"
                icon="ri-camera-switch-line"
                onClick={() => setFacing((prev) => (prev === "user" ? "environment" : "user"))}
              >
                Changer de caméra
              </Button>
            )}
            <Button icon="ri-camera-fill" onClick={take} disabled={!ready}>
              Prendre la photo
            </Button>
          </>
        )
      }
    >
      <div className="relative mx-auto aspect-square w-full max-w-sm overflow-hidden rounded-lg bg-foreground-950">
        {shot ? (
          <img src={shot} alt="Photo prise" className="h-full w-full object-cover" />
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              onLoadedData={() => setReady(true)}
              className={`h-full w-full object-cover ${mirrored ? "-scale-x-100" : ""}`}
            />
            {!ready && !error && (
              <p className="absolute inset-0 flex items-center justify-center text-sm text-background-100">
                Ouverture de la caméra...
              </p>
            )}
            {/* Repère du visage. */}
            {ready && (
              <span
                className="pointer-events-none absolute left-1/2 top-[44%] h-[62%] w-[48%] -translate-x-1/2 -translate-y-1/2 rounded-[50%] border-2 border-dashed border-background-50/70"
                aria-hidden="true"
              ></span>
            )}
          </>
        )}
      </div>
      {error && (
        <p role="alert" className="mt-3 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-sm text-accent-800">
          {error}
        </p>
      )}
    </Modal>
  );
}
